import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MapPin, X } from "lucide-react";
import { COUNTIES, getCountyByName, normalizeCountyName } from "./KenyaCounties";
import { CountyLogo } from "./CountySelectDialog";

// Predictive county field for forms (used in the seller application).
//
//  - Type "B" and it lists every county starting with B; type "Bu" and it
//    narrows to Bungoma and Busia, and so on.
//  - The seller must CLICK (or press Enter on) a suggestion. `value` only
//    ever holds an exact county name from the list, or "" — so a half-typed
//    or misspelled county can never be submitted.
//  - Typing again after choosing clears the choice, so the field can't show
//    one county while holding another.
//
// The suggestion list is rendered through a portal into the app's themed root
// (the element with data-theme), positioned with `fixed` from the input's
// on-screen rect. That way it isn't clipped by the Stepper's overflow, and it
// still picks up the app's CSS variables in both dark and light mode.

const INPUT_CLASS =
  "w-full bg-[var(--bg-container)] border border-[var(--border-soft)] rounded-xl pl-11 pr-10 py-3 text-sm outline-none focus:border-[#FFB800] text-[var(--text-primary)] placeholder-gray-500 transition-colors";

function rankMatches(query: string) {
  const q = normalizeCountyName(query);
  if (!q) return COUNTIES;

  // 1) counties whose name starts with what was typed ("Bu" -> Bungoma, Busia)
  const startsWith = COUNTIES.filter((c) =>
    normalizeCountyName(c.name).startsWith(q)
  );
  if (startsWith.length > 0) return startsWith;

  // 2) otherwise any word inside the name starts with it ("river" -> Tana River)
  const wordStart = COUNTIES.filter((c) =>
    c.name
      .toLowerCase()
      .split(/[\s-]+/)
      .some((w) => normalizeCountyName(w).startsWith(q))
  );
  if (wordStart.length > 0) return wordStart;

  // 3) last resort: contains it anywhere
  return COUNTIES.filter((c) => normalizeCountyName(c.name).includes(q));
}

export interface CountyAutocompleteInputProps {
  // Chosen county name, or "" when nothing valid is chosen yet.
  value: string;
  onChange: (countyName: string) => void;
  // Lets the parent scroll to / focus the field when validation fails.
  inputRef?: React.RefObject<HTMLInputElement>;
  // Extra classes appended to the input (e.g. the red "invalid" ring).
  invalidClassName?: string;
  placeholder?: string;
}

export default function CountyAutocompleteInput({
  value,
  onChange,
  inputRef,
  invalidClassName = "",
  placeholder = "Type in your county, e.g. Bungoma",
}: CountyAutocompleteInputProps) {
  const ownRef = useRef<HTMLInputElement>(null);
  const el = inputRef || ownRef;
  const listId = useId();

  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{
    left: number;
    width: number;
    top?: number;
    bottom?: number;
    maxHeight: number;
  } | null>(null);

  // Keep the visible text in step if the parent sets a value directly.
  useEffect(() => {
    if (value && value !== text) setText(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const suggestions = useMemo(() => rankMatches(text), [text]);
  const selected = getCountyByName(value);

  useEffect(() => setActive(0), [text]);

  // Position the list under (or, if the on-screen keyboard leaves no room,
  // above) the input, and keep it attached while the page scrolls.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const input = el.current;
      if (!input) return;
      const r = input.getBoundingClientRect();
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const below = vh - r.bottom - 12;
      const above = r.top - 12;
      if (below < 190 && above > below) {
        setPos({
          left: r.left,
          width: r.width,
          bottom: window.innerHeight - r.top + 6,
          maxHeight: Math.max(120, Math.min(260, above)),
        });
      } else {
        setPos({
          left: r.left,
          width: r.width,
          top: r.bottom + 6,
          maxHeight: Math.max(120, Math.min(260, below)),
        });
      }
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
    };
  }, [open, el]);

  const choose = (name: string) => {
    setText(name);
    onChange(name);
    setOpen(false);
  };

  // Typed text that is exactly a county ("nairobi", "Nairobi City") counts as
  // choosing it, so nobody gets stuck on an unclicked-but-correct entry.
  const exactMatch = (raw: string) => {
    const q = normalizeCountyName(raw);
    if (!q) return undefined;
    return COUNTIES.find((c) => normalizeCountyName(c.name) === q);
  };

  const handleChange = (raw: string) => {
    setText(raw);
    setOpen(true);
    if (value) onChange(""); // typing again undoes the previous choice
  };

  const handleBlur = () => {
    setOpen(false);
    if (!value) {
      const m = exactMatch(text);
      if (m) choose(m.name);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (open && suggestions[active]) {
        e.preventDefault();
        choose(suggestions[active].name);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Keep the highlighted option in view while arrowing through the list.
  useEffect(() => {
    if (!open) return;
    document
      .getElementById(`${listId}-opt-${active}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active, open, listId]);

  const host =
    (el.current?.closest("[data-theme]") as HTMLElement | null) ||
    (typeof document !== "undefined" ? document.body : null);

  const showError = text.trim().length > 0 && !value && !open;
  const typedLen = text.trim().length;

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
        {selected ? (
          <CountyLogo county={selected} size={24} />
        ) : (
          <MapPin size={18} className="text-[var(--text-muted)]" />
        )}
      </span>

      <input
        ref={el}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open ? `${listId}-opt-${active}` : undefined}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        placeholder={placeholder}
        value={text}
        required
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          setOpen(true);
          // Bring the field clear of the on-screen keyboard.
          setTimeout(
            () =>
              el.current?.scrollIntoView?.({
                behavior: "smooth",
                block: "center",
              }),
            250
          );
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={INPUT_CLASS + invalidClassName}
      />

      {text && (
        <button
          type="button"
          aria-label="Clear county"
          // mousedown (not click) so the input doesn't blur first
          onMouseDown={(e) => {
            e.preventDefault();
            setText("");
            onChange("");
            setOpen(true);
            el.current?.focus();
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        >
          <X size={16} />
        </button>
      )}

      {showError && (
        <p className="text-[11px] text-[#FF3B5C] mt-1.5 ml-1">
          Please choose your county from the list.
        </p>
      )}

      {open && pos && host &&
        createPortal(
          <ul
            id={listId}
            role="listbox"
            className="fixed z-[300] overflow-y-auto overscroll-contain rounded-xl border border-[var(--border-soft)] bg-[var(--bg-container)] shadow-2xl shadow-black/40 py-1"
            style={{
              left: pos.left,
              width: pos.width,
              top: pos.top,
              bottom: pos.bottom,
              maxHeight: pos.maxHeight,
            }}
          >
            {suggestions.length === 0 ? (
              <li className="px-4 py-3 text-sm text-[var(--text-muted)]">
                No county matches &ldquo;{text.trim()}&rdquo;
              </li>
            ) : (
              suggestions.map((c, i) => {
                const startsWithTyped =
                  typedLen > 0 &&
                  c.name.toLowerCase().startsWith(text.trim().toLowerCase());
                return (
                  <li
                    key={c.id}
                    id={`${listId}-opt-${i}`}
                    role="option"
                    aria-selected={i === active}
                    // mousedown + preventDefault: select before the input blurs
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(c.name);
                    }}
                    onMouseEnter={() => setActive(i)}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm ${
                      i === active ? "bg-[var(--surface-soft)]" : ""
                    }`}
                  >
                    <CountyLogo county={c} size={28} />
                    <span className="text-[var(--text-primary)]">
                      {startsWithTyped ? (
                        <>
                          <span className="font-bold">
                            {c.name.slice(0, typedLen)}
                          </span>
                          {c.name.slice(typedLen)}
                        </>
                      ) : (
                        c.name
                      )}
                    </span>
                  </li>
                );
              })
            )}
          </ul>,
          host
        )}
    </div>
  );
}
